import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import type { PrivacyPolicyPage } from '../types/content'
import { formatRuDate } from '../utils/formatDate'

interface PrivacyPolicyProps {
  data: PrivacyPolicyPage
}

export default function PrivacyPolicy({ data }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-dark">
      <div className="container mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-primary transition-colors mb-8"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {data.backButton}
          </Link>

          <article className="card p-8 md:p-12">
            <h1 className="text-2xl md:text-3xl font-bold text-gradient mb-4">
              {data.title}
            </h1>

            <p className="text-sm text-gray-500 mb-8">
              Редакция от {formatRuDate(data.lastUpdated)}
            </p>

            <p className="text-gray-300 leading-relaxed mb-12 max-w-[70ch]">
              {data.intro}
            </p>

            <div className="flex flex-col gap-10">
              {data.sections.map((section, index) => (
                <motion.section
                  key={section.number}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(0.05 * index, 0.4) }}
                >
                  <h2 className="flex items-baseline gap-3 text-lg md:text-xl font-semibold text-white mb-4">
                    <span className="text-primary tabular-nums">{section.number}.</span>
                    {section.title}
                  </h2>

                  <ul className="flex flex-col gap-3 pl-0">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex gap-3 text-gray-400 leading-relaxed">
                        <span className="text-primary/50 shrink-0 tabular-nums text-sm pt-1">
                          {section.number}.{i + 1}
                        </span>
                        <span className="max-w-[70ch]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.section>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-border">
              <h2 className="text-base font-semibold text-white mb-4">
                {data.contactsTitle}
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                {data.company.name}<br />
                ОГРН: {data.company.ogrn} &middot; ИНН: {data.company.inn}<br />
                {data.company.address}<br />
                <a
                  href={`mailto:${data.company.email}`}
                  className="text-primary hover:text-primary-light transition-colors"
                >
                  {data.company.email}
                </a>
                {' · '}
                <a
                  href={`tel:${data.company.phone.replace(/[^\d+]/g, '')}`}
                  className="text-primary hover:text-primary-light transition-colors"
                >
                  {data.company.phone}
                </a>
              </p>
            </div>
          </article>
        </motion.div>
      </div>
    </div>
  )
}
